import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import { Ban, Lock, LogOut, Mail, RotateCcw, ShieldCheck, UserPlus } from "lucide-react";
import { toast } from "sonner";
import type { AdminTeamMember, PlatformAdminRole } from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogFooter, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { QueryError } from "@/components/query-error";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useAdminMeQuery,
  useChangeAdminRoleMutation,
  useInviteAdminMutation,
  useIsSuperAdmin,
  useResendInviteMutation,
  useRevokeAdminSessionsMutation,
  useSetAdminStatusMutation,
  useTeamQuery,
} from "@/features/api";
import { showApiError } from "@/lib/api-client";
import { ADMIN_ROLE_LABELS, dateTime } from "@/lib/format";

const ROLES: PlatformAdminRole[] = ["SuperAdmin", "Support"];

const ROLE_DESCRIPTIONS: Record<PlatformAdminRole, string> = {
  SuperAdmin:
    "Komisyon, askıya alma, ödemeler, destek oturumu ve ekip yönetimi dahil her şeyi yapabilir.",
  Support: "Her şeyi görür, hiçbir şeyi değiştiremez.",
};

type ConfirmAction =
  | { kind: "role"; member: AdminTeamMember; role: PlatformAdminRole }
  | { kind: "disable" | "enable" | "revoke"; member: AdminTeamMember };

export function TeamPage() {
  const isSuper = useIsSuperAdmin();
  const { data: me } = useAdminMeQuery();
  const { data, isLoading, error, refetch, isRefetching } = useTeamQuery(isSuper);
  const resend = useResendInviteMutation();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmAction | null>(null);

  if (!isSuper) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          <Lock className="size-8 text-muted-foreground" />
          <div className="space-y-1">
            <h1 className="text-lg font-semibold">Ekip yönetimi süper admin'e açık</h1>
            <p className="text-sm text-muted-foreground">
              Destek rolü ekibi göremez ve değiştiremez. Yetki gerekiyorsa bir süper admin'e yaz.
            </p>
          </div>
          <Button asChild variant="outline">
            <Link to="/">Özete dön</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const onlySelf = data !== undefined && data.every((m) => m.id === me?.id);

  async function onResend(member: AdminTeamMember) {
    try {
      await resend.mutateAsync(member.id);
      toast.success(`Davet yeniden gönderildi: ${member.email}`);
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Ekip</h1>
          <p className="text-sm text-muted-foreground">
            Admin'ler silinmez, pasif yapılır. Davet linki 24 saat geçerli; yeni bir IP'den girişte
            e-postaya kod gider. "Oturumları kapat" tanınan IP'leri de unutturur.
          </p>
        </div>
        <Button onClick={() => setInviteOpen(true)}>
          <UserPlus className="size-4" />
          Admin ekle
        </Button>
      </div>

      <Card className="py-0">
        <CardContent className="px-0">
          {error ? (
            <QueryError
              className="m-6"
              message="Ekip yüklenemedi."
              onRetry={() => refetch()}
              retrying={isRefetching}
            />
          ) : isLoading || !data ? (
            <p className="p-6 text-sm text-muted-foreground">Yükleniyor...</p>
          ) : (
            <ul className="divide-y text-sm">
              {data.map((m) => (
                <TeamRow
                  key={m.id}
                  member={m}
                  isSelf={m.id === me?.id}
                  onConfirm={setConfirm}
                  onResend={() => onResend(m)}
                  resendPending={resend.isPending && resend.variables === m.id}
                />
              ))}
              {onlySelf && (
                <li className="px-6 py-4 text-muted-foreground">
                  Ekipte şimdilik yalnız sen varsın. "Admin ekle" ile süper admin veya salt okuma
                  yetkili destek davet edebilirsin.
                </li>
              )}
            </ul>
          )}
        </CardContent>
      </Card>

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} />
      <ConfirmDialog action={confirm} selfId={me?.id} onClose={() => setConfirm(null)} />
    </div>
  );
}

function TeamRow({
  member,
  isSelf,
  onConfirm,
  onResend,
  resendPending,
}: {
  member: AdminTeamMember;
  isSelf: boolean;
  onConfirm: (action: ConfirmAction) => void;
  onResend: () => void;
  resendPending: boolean;
}) {
  const active = member.status === "Active";
  const pendingInvite = active && !member.passwordSet;
  const inviteExpired =
    pendingInvite && member.inviteExpiresAt !== null && new Date(member.inviteExpiresAt) < new Date();
  const canRevoke = isSelf ? member.activeSessionCount > 1 : member.activeSessionCount > 0;
  const otherRole: PlatformAdminRole = member.role === "SuperAdmin" ? "Support" : "SuperAdmin";

  return (
    <li className="flex flex-wrap items-start justify-between gap-4 px-6 py-4">
      <div className="min-w-0 space-y-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{member.displayName}</span>
          {isSelf && <span className="text-muted-foreground">(sen)</span>}
          <Badge variant="outline">{ADMIN_ROLE_LABELS[member.role]}</Badge>
          {!active ? (
            <Badge variant="secondary">Pasif</Badge>
          ) : pendingInvite ? (
            <Badge variant="warning">{inviteExpired ? "Davet süresi doldu" : "Davet bekliyor"}</Badge>
          ) : (
            <Badge variant="success">Aktif</Badge>
          )}
        </div>
        <div className="text-xs break-all text-muted-foreground">{member.email}</div>
        <div className="text-xs text-muted-foreground">
          {pendingInvite && !inviteExpired && member.inviteExpiresAt
            ? `Davet linki ${dateTime(member.inviteExpiresAt)} tarihine kadar geçerli`
            : `Son giriş: ${dateTime(member.lastLoginAt)}`}
          {member.activeSessionCount > 0 && ` · ${member.activeSessionCount} açık oturum`}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {pendingInvite && (
          <Button variant="outline" size="sm" onClick={onResend} disabled={resendPending}>
            <Mail className="size-4" />
            {resendPending ? "Gönderiliyor..." : "Daveti yeniden gönder"}
          </Button>
        )}
        {!isSelf && active && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => onConfirm({ kind: "role", member, role: otherRole })}
          >
            <ShieldCheck className="size-4" />
            {ADMIN_ROLE_LABELS[otherRole]} yap
          </Button>
        )}
        {canRevoke && (
          <Button variant="outline" size="sm" onClick={() => onConfirm({ kind: "revoke", member })}>
            <LogOut className="size-4" />
            {isSelf ? "Diğer oturumlarımı kapat" : "Oturumları kapat"}
          </Button>
        )}
        {!isSelf &&
          (active ? (
            <Button
              variant="outline"
              size="sm"
              className="text-destructive"
              onClick={() => onConfirm({ kind: "disable", member })}
            >
              <Ban className="size-4" />
              Pasif yap
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => onConfirm({ kind: "enable", member })}>
              <RotateCcw className="size-4" />
              Aktif yap
            </Button>
          ))}
      </div>
    </li>
  );
}

function InviteDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const invite = useInviteAdminMutation();
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [role, setRole] = useState<PlatformAdminRole>("Support");

  const valid = /^\S+@\S+\.\S+$/.test(email.trim()) && displayName.trim().length >= 2;

  function close() {
    onOpenChange(false);
    setEmail("");
    setDisplayName("");
    setRole("Support");
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    try {
      const member = await invite.mutateAsync({
        email: email.trim(),
        displayName: displayName.trim(),
        role,
      });
      toast.success(`Davet gönderildi: ${member.email}`);
      close();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? onOpenChange(true) : close())} disableBackdropClose>
      <DialogContent>
        <DialogHeader
          title="Admin ekle"
          description="E-postasına 24 saat geçerli bir şifre belirleme linki gider. Şifreyi belirleyince giriş yapabilir."
        />
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="invite-email">E-posta</Label>
            <Input
              id="invite-email"
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="invite-name">Ad soyad</Label>
            <Input
              id="invite-name"
              maxLength={120}
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label>Rol</Label>
            <Select value={role} onValueChange={(v) => setRole(v as PlatformAdminRole)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((r) => (
                  <SelectItem key={r} value={r}>
                    {ADMIN_ROLE_LABELS[r]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{ROLE_DESCRIPTIONS[role]}</p>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={close}>
              Vazgeç
            </Button>
            <Button type="submit" disabled={!valid || invite.isPending}>
              {invite.isPending ? "Gönderiliyor..." : "Davet gönder"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmDialog({
  action,
  selfId,
  onClose,
}: {
  action: ConfirmAction | null;
  selfId: string | undefined;
  onClose: () => void;
}) {
  const changeRole = useChangeAdminRoleMutation();
  const setStatus = useSetAdminStatusMutation();
  const revoke = useRevokeAdminSessionsMutation();
  const pending = changeRole.isPending || setStatus.isPending || revoke.isPending;

  const copy = action ? describe(action, action.member.id === selfId) : null;

  async function onConfirm() {
    if (!action) return;
    const { member } = action;
    try {
      switch (action.kind) {
        case "role":
          await changeRole.mutateAsync({ id: member.id, role: action.role });
          toast.success(`${member.displayName} artık ${ADMIN_ROLE_LABELS[action.role]}.`);
          break;
        case "disable":
          await setStatus.mutateAsync({ id: member.id, active: false });
          toast.success(`${member.displayName} pasif yapıldı.`);
          break;
        case "enable":
          await setStatus.mutateAsync({ id: member.id, active: true });
          toast.success(`${member.displayName} tekrar aktif.`);
          break;
        case "revoke":
          await revoke.mutateAsync(member.id);
          toast.success("Oturumlar kapatıldı.");
          break;
      }
      onClose();
    } catch (err) {
      showApiError(err);
    }
  }

  return (
    <Dialog open={action !== null} onOpenChange={(next) => !next && onClose()}>
      <DialogContent>
        {copy && <DialogHeader title={copy.title} description={copy.description} />}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Vazgeç
          </Button>
          <Button
            variant={action?.kind === "disable" ? "destructive" : "default"}
            onClick={onConfirm}
            disabled={pending}
          >
            {pending ? "Kaydediliyor..." : (copy?.confirm ?? "Onayla")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function describe(
  action: ConfirmAction,
  isSelf: boolean,
): { title: string; description: string; confirm: string } {
  const name = action.member.displayName;
  switch (action.kind) {
    case "role":
      return {
        title: `${name} ${ADMIN_ROLE_LABELS[action.role]} olsun mu?`,
        description: `${ROLE_DESCRIPTIONS[action.role]} Açık oturumlarında hemen geçerli olur.`,
        confirm: "Rolü değiştir",
      };
    case "disable":
      return {
        title: `${name} pasif yapılsın mı?`,
        description:
          "Panele giremez; açık admin oturumları ve destek oturumları hemen kapanır. Kayıtları silinmez, tekrar aktif yapılabilir.",
        confirm: "Pasif yap",
      };
    case "enable":
      return {
        title: `${name} tekrar aktif olsun mu?`,
        description: action.member.passwordSet
          ? "Şifresiyle tekrar giriş yapabilir."
          : "Şifresini henüz belirlemedi; aktif yaptıktan sonra daveti yeniden gönder.",
        confirm: "Aktif yap",
      };
    case "revoke":
      return isSelf
        ? {
            title: "Diğer oturumların kapatılsın mı?",
            description:
              "Bu tarayıcıdaki oturum dışındaki tüm admin oturumların ve açık destek oturumların kapanır.",
            confirm: "Oturumları kapat",
          }
        : {
            title: `${name} oturumları kapatılsın mı?`,
            description:
              "Açık admin oturumları ve destek oturumları kapanır; tekrar giriş yapması gerekir. Hesabı pasif olmaz.",
            confirm: "Oturumları kapat",
          };
  }
}
